import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Input, Empty } from "../components/UI";
import { CocoaSelect } from "../components/CocoaSelect";
import { usePagination, PaginationBar } from "../components/Pagination";
import { Search, RefreshCw, ExternalLink, PartyPopper, ShieldAlert, AlertTriangle, Users, FilterX, BellRing, MailPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const BAND_STYLE = { red: "bg-[#FFEBEE] text-[#C62828]", amber: "bg-[#FFF8E1] text-[#876600]" };

export default function ChartGaps() {
  useEffect(() => { document.title = "Chart Gaps | COCOA EHR"; }, []);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSupervisor = user && (user.role === "admin" || user.is_super_admin);
  const [data, setData] = useState(null);
  const [band, setBand] = useState("");
  const [clinF, setClinF] = useState("");
  const [gapF, setGapF] = useState("");
  const [q, setQ] = useState("");
  const [nudging, setNudging] = useState({});
  const [nudged, setNudged] = useState({});
  const load = () => api.get("/chart-gaps").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const sendNudge = async (row) => {
    if (!row.clinician_id) return toast.error("This client has no assigned clinician to nudge");
    setNudging((s) => ({ ...s, [row.client_id]: true }));
    try {
      const r = await api.post(`/chart-gaps/nudge/${row.client_id}`);
      const dc = r?.data?.digest_count;
      toast.success(dc && dc > 1 ? `Added to ${row.clinician_name}'s digest (${dc} clients today)` : `Nudge sent to ${row.clinician_name}`);
      setNudged((s) => ({ ...s, [row.client_id]: true }));
    } catch (e) {
      const msg = apiError(e);
      toast.error(msg);
      if (e?.response?.status === 429) {
        setNudged((s) => ({ ...s, [row.client_id]: true }));
      }
    }
    setNudging((s) => ({ ...s, [row.client_id]: false }));
  };

  const clinicians = useMemo(() => (data ? [...new Map(data.rows.map((r) => [r.clinician_id || "none", r.clinician_name])).entries()] : []), [data]);
  const gapTypes = useMemo(() => (data ? [...new Set(data.rows.flatMap((r) => r.gaps))] : []), [data]);
  const shown = useMemo(() => {
    if (!data) return [];
    const qq = q.trim().toLowerCase();
    return data.rows.filter((r) => (!band || r.band === band) &&
      (!clinF || (r.clinician_id || "none") === clinF) &&
      (!gapF || r.gaps.includes(gapF)) &&
      (!qq || `${r.client_name} ${r.mrn} ${r.clinician_name}`.toLowerCase().includes(qq)));
  }, [data, band, clinF, gapF, q]);
  const pg = usePagination(shown);
  const hasFilters = !!(band || clinF || gapF || q.trim());
  const clearFilters = () => { setBand(""); setClinF(""); setGapF(""); setQ(""); };

  if (!data) return <div className="space-y-4"><div className="h-24 animate-pulse rounded-2xl bg-[#EFEBE9]" /><div className="h-64 animate-pulse rounded-2xl bg-[#EFEBE9]" /></div>;

  return (
    <div data-testid="chart-gaps-page">
      <PageHeader title="Chart Gaps Workqueue" subtitle={`${data.total} active client${data.total === 1 ? "" : "s"} below the 80% chart completeness bar`}
        action={
          <div className="flex items-center gap-2">
            {isSupervisor && (
              <Btn variant="outline" data-testid="onboarding-reminder-scan"
                onClick={async () => {
                  try {
                    const r = await api.post("/onboarding-reminders/scan");
                    const d = r.data || {};
                    if (d.sent > 0) toast.success(`Sent ${d.sent} onboarding reminder${d.sent === 1 ? "" : "s"}${d.skipped_on_cooldown ? `, ${d.skipped_on_cooldown} still on cooldown` : ""}`);
                    else toast.info(d.skipped_on_cooldown > 0 ? `Nothing to send · ${d.skipped_on_cooldown} still on cooldown` : "No stale consents right now");
                  } catch (e) { toast.error(apiError(e)); }
                }}>
                <MailPlus size={14} /> Send onboarding reminders
              </Btn>
            )}
            <Btn variant="outline" data-testid="chart-gaps-refresh" onClick={() => { load(); toast.success("Refreshed"); }}><RefreshCw size={14} /> Refresh</Btn>
          </div>
        } />

      <div className="mb-5 grid grid-cols-3 gap-3" data-testid="chart-gaps-summary">
        {[["red", "Critical (below 50%)", data.red, ShieldAlert, "bg-[#FFEBEE] text-[#C62828]"],
          ["amber", "Needs work (50-79%)", data.amber, AlertTriangle, "bg-[#FFF8E1] text-[#876600]"],
          ["", "Total in workqueue", data.total, Users, "bg-[#EFEBE0] text-[#4E342E]"]].map(([key, label, val, Icon, tone]) => (
          <button key={label} data-testid={`chart-gaps-band-${key || "all"}`} onClick={() => setBand(band === key ? "" : key)}
            className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${band === key && key ? "border-[#4E342E] bg-[#F5F2EB]" : "border-[#EFEBE0] bg-white hover:bg-[#FDFCF8]"}`}>
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone}`}><Icon size={17} /></span>
            <span>
              <span className="block font-heading text-xl font-bold leading-tight text-[#3E2723]">{val}</span>
              <span className="block text-[11px] leading-tight text-[#7B6157]">{label}</span>
            </span>
          </button>
        ))}
      </div>

      <Card className="p-4">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#84675B]" />
            <Input data-testid="chart-gaps-search" className="h-10 w-56 pl-9" placeholder="Search client, MRN, clinician…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <CocoaSelect testId="chart-gaps-clinician-filter" className="w-48" placeholder="All clinicians" value={clinF} onChange={setClinF}
            options={clinicians.map(([id, name]) => [id, name])} />
          <CocoaSelect testId="chart-gaps-gap-filter" className="w-64" placeholder="All gap types" value={gapF} onChange={setGapF}
            options={gapTypes.map((g) => [g, g])} />
          {hasFilters && (
            <button data-testid="chart-gaps-clear-filters" onClick={clearFilters}
              className="flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold text-[#7B6157] hover:bg-[#F5F2EB]"><FilterX size={13} /> Clear filters</button>
          )}
        </div>
        {data.total === 0 ? (
          <div className="p-8 text-center" data-testid="chart-gaps-empty">
            <PartyPopper size={32} className="mx-auto mb-2 text-[#2E7D32]" />
            <div className="font-heading text-lg font-bold text-[#3E2723]">No chart gaps</div>
            <div className="text-sm text-[#7B6157]">Every active chart is at least 80% complete. Outstanding work, team.</div>
          </div>
        ) : shown.length === 0 ? <Empty text="No clients match your filters" /> : (
          <div className="space-y-2">
            {pg.pageRows.map((r) => (
              <div key={r.client_id} data-testid={`chart-gap-row-${r.client_id}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-[#F1EEE7] px-4 py-3">
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold tabular-nums ${BAND_STYLE[r.band]}`}>{r.score}%</span>
                <div className="min-w-0">
                  <div className="truncate text-sm font-bold text-[#3E2723]">{r.client_name} {r.mrn && <span className="font-normal text-[#84675B]">· {r.mrn}</span>}</div>
                  <div className="text-[11px] text-[#7B6157]">Clinician: {r.clinician_name}</div>
                </div>
                <div className="flex min-w-0 flex-1 flex-wrap justify-end gap-1">
                  {r.gaps.map((g) => (
                    <span key={g} className="rounded-full bg-[#F5F2EB] px-2 py-0.5 text-[10px] font-semibold text-[#6D4C41]">{g}</span>
                  ))}
                </div>
                {isSupervisor && (
                  <button data-testid={`chart-gap-nudge-${r.client_id}`} onClick={() => sendNudge(r)}
                    disabled={!r.clinician_id || nudging[r.client_id] || nudged[r.client_id]}
                    title={r.clinician_id ? `Send a nudge to ${r.clinician_name}` : "No assigned clinician"}
                    className={`flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                      nudged[r.client_id] ? "bg-[#E8F5E9] text-[#2E7D32]"
                      : !r.clinician_id ? "border border-[#EFEBE0] text-[#84675B] cursor-not-allowed"
                      : "border border-[#FFECB3] bg-[#FFF8E1] text-[#876600] hover:bg-[#FFECB3]"}`}>
                    <BellRing size={12} /> {nudged[r.client_id] ? "Nudged" : nudging[r.client_id] ? "Sending…" : "Nudge"}
                  </button>
                )}
                <button data-testid={`chart-gap-open-${r.client_id}`} onClick={() => navigate(`/clients/${r.client_id}`)}
                  className="flex shrink-0 items-center gap-1 rounded-full border border-[#E5DFD3] px-3 py-1.5 text-xs font-bold text-[#6D4C41] hover:bg-[#F5F2EB]"><ExternalLink size={12} /> Open Chart</button>
              </div>
            ))}
          </div>
        )}
        <PaginationBar {...pg} testId="chart-gaps-pagination" />
      </Card>
    </div>
  );
}
